// client/src/hooks/useBooks.js
import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchBooksAPI, addBookAPI, deleteBookAPI } from '../services/api';
import { useAuth } from './useAuth';
import useDebounce from './useDebounce';

export const useBooks = () => {
  const [books, setBooks] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ sortBy: 'title', sortOrder: 'asc' });
  const [addressFilter, setAddressFilter] = useState(null);
  const [roomFilter, setRoomFilter] = useState(null);
  const [shelfFilter, setShelfFilter] = useState(null);
  const [compartmentFilter, setCompartmentFilter] = useState(null);
  // *** NEU: State für Rating-Filter ***
  const [ratingFilter, setRatingFilter] = useState(null); // null = Alle, 'read', 'wants_to_read', 'keep', 'discard'

  const { handleApiError } = useAuth();
  const isMounted = useRef(true);
  const isInitialMount = useRef(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Der zentrale Effekt zum Laden der Daten
  useEffect(() => {
    if (!isMounted.current) return;

    setIsLoading(true);
    setError(null);
    console.log(`EFFECT Fetching books: page=${currentPage}, search=${debouncedSearchTerm}, sort=${sortConfig.sortBy}:${sortConfig.sortOrder}, filters=`, { addressFilter, roomFilter, shelfFilter, compartmentFilter, ratingFilter }); // <-- ratingFilter hinzugefügt

    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: '10',
      sortBy: sortConfig.sortBy,
      sortOrder: sortConfig.sortOrder,
    });
    if (debouncedSearchTerm) params.append('searchTerm', debouncedSearchTerm);
    if (addressFilter) params.append('adresse', addressFilter);
    if (roomFilter) params.append('raum', roomFilter);
    if (shelfFilter) params.append('regal', shelfFilter);
    if (compartmentFilter) params.append('fach', compartmentFilter);
    // *** NEU: Rating-Filter hinzufügen, wenn gesetzt ***
    if (ratingFilter) params.append('ratingFilter', ratingFilter);

    console.log('useBooks.js: useEffect - Final Params:', params.toString());

    fetchBooksAPI(params)
      .then(data => {
        if (isMounted.current) {
          setBooks(data.books);
          setTotalPages(data.totalPages);
        }
      })
      .catch(err => {
        if (isMounted.current) {
          handleApiError(err);
          setError(`Fehler beim Laden der Bücher: ${err.message}`);
        }
      })
      .finally(() => {
        if (isMounted.current) {
          setIsLoading(false);
          isInitialMount.current = false;
        }
      });

  // *** NEU: ratingFilter als Abhängigkeit hinzufügen ***
  }, [currentPage, debouncedSearchTerm, sortConfig, addressFilter, roomFilter, shelfFilter, compartmentFilter, ratingFilter, handleApiError]);


  // --- Handler ---
  const handleSearch = useCallback((newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleSortChange = useCallback((newSortBy) => {
    setSortConfig(prevConfig => {
        let newSortOrder = 'asc';
        if (prevConfig.sortBy === newSortBy && prevConfig.sortOrder === 'asc') {
          newSortOrder = 'desc';
        }
        return { sortBy: newSortBy, sortOrder: newSortOrder };
    });
    setCurrentPage(1); // Reset page on sort change
  }, []);

  const handleHierarchicalFilterChange = useCallback((level, value) => {
    if (level === 'address') {
        setAddressFilter(value);
        setRoomFilter(null);
        setShelfFilter(null);
        setCompartmentFilter(null);
    } else if (level === 'room') {
        setRoomFilter(value);
        setShelfFilter(null);
        setCompartmentFilter(null);
    } else if (level === 'shelf') {
        setShelfFilter(value);
        setCompartmentFilter(null);
    } else if (level === 'compartment') {
        setCompartmentFilter(value);
    }
    setCurrentPage(1); // Reset page on filter change
  }, []);

  // *** NEU: Handler für Rating-Filter ***
  const handleRatingFilterChange = useCallback((newFilter) => {
    setRatingFilter(newFilter); // Setze den neuen Filter (kann null sein)
    setCurrentPage(1); // Setze Seite auf 1 zurück
  }, []);


  // --- Aktionen (add, delete, reload) ---
  const reloadBooks = useCallback(() => {
      setCurrentPage(prev => prev === 1 ? 1 : prev);
  }, []);

  const addBook = useCallback(async (bookData) => {
    try {
      await addBookAPI(bookData);
      reloadBooks(); // Löst Neuladen aus
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }, [reloadBooks, handleApiError]);

  const deleteBook = useCallback(async (id) => {
    try {
      await deleteBookAPI(id);
       // Prüfen, ob es das letzte Buch auf der Seite war (optional)
       if (books.length === 1 && currentPage > 1) {
           setCurrentPage(prev => prev - 1); // Gehe zur vorherigen Seite
       } else {
           reloadBooks(); // Lade aktuelle Seite neu
       }
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }, [reloadBooks, handleApiError, books.length, currentPage]);


  return {
    books, totalPages, currentPage, isLoading, error, searchTerm,
    sortConfig, addressFilter, roomFilter, shelfFilter, compartmentFilter,
    // *** NEU: Rating-Filter und Handler exportieren ***
    ratingFilter,
    handleRatingFilterChange,
    // ---
    handleSearch, handlePageChange, handleSortChange, handleHierarchicalFilterChange,
    addBook, deleteBook, reloadBooks,
  };
};
